import clsx from "clsx";
import type { ReactNode, InputHTMLAttributes } from "react";
import { forwardRef, useState } from "react";
import EyeOpenIcon from "assets/password-show.svg?react";
import EyeClosedIcon from "assets/password-hide.svg?react";
import styles from "./index.module.css";

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  icon?: ReactNode;
  iconAlt?: string;
  containerClassName?: string;
  inputClassName?: string;
  iconClassName?: string;
  toggleButtonClassName?: string;
  initialVisibility?: boolean;
  label?: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      icon,
      containerClassName,
      inputClassName,
      iconClassName,
      toggleButtonClassName,
      initialVisibility = false,
      style,
      label,
      value,
      id,
      ...inputProps
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(initialVisibility);

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    return (
      <>
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
        )}
        <div
          className={clsx(styles.inputGroup, containerClassName)}
          style={style}
          data-empty={!value}
        >
          {icon && <div className={clsx(styles.inputIcon, iconClassName)}>{icon && icon}</div>}

          <input
            id={id}
            className={clsx(styles.input, icon && styles.inputWithIcon, inputClassName)}
            ref={ref}
            type={showPassword ? "text" : "password"}
            value={value}
            {...inputProps}
          />
          <button
            aria-label={showPassword ? "Hide password" : "Show password"}
            className={clsx(styles.passwordToggle, toggleButtonClassName)}
            onClick={togglePasswordVisibility}
            type="button"
          >
            {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
          </button>
        </div>
      </>
    );
  },
);

PasswordInput.displayName = "PasswordInput";
